import { AppComponentProps, NumberKeyedDictionary } from '../typescriptHelpers';
import * as React from 'react';
import { DisallowedTokensForBranch, ReleaseBranchPattern, requireAtLeastOneOfTokensForBranch } from '../types';
import {
    NewReleaseBranchPatternRequest,
    DeleteReleaseBranchPatternRequest,
    UpdateReleaseBranchPatternRequest,
    tryUpdateAppSettings,
} from '../actions/appSettings';
import { PlusOutlined } from '@ant-design/icons';
import { RootState } from '../reducers/reducers';
import { getError, getIsLoading, getUpdated, validatePattern } from '../helpers';
import { AppSettingsUpdatable } from '../actions/updatable';
import { connect, ConnectedProps } from 'react-redux';
import NewReleaseBranchPatternModal from './NewReleaseBranchPatternModal';
import { forEach, forIn } from 'lodash';
import { AppSettingsEditableInputComponent, AppSettingsActionsComponent } from './AppSettingsTableActionsComponent';
import { runwayColors } from './styles/globalStyles';
import styles from './styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Table from 'antd/lib/table';
import Typography from 'antd/lib/typography';
import Space from 'antd/lib/space';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { selectApp, selectAuthCredentials } from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import { AppEntityRequest } from '../actions/entities';
import * as API from '../api';

interface State {
    newReleaseBranchModalVisible: boolean;
    releaseBranchIsEditing: NumberKeyedDictionary<boolean>;
    releaseBranchPatternUpdated: NumberKeyedDictionary<string>;
    releaseBranchPatternIsValid: NumberKeyedDictionary<boolean>;
    suggestedBranchPattern: string;
    isSuggestedBranchPatternLoading: boolean;
}

class ReleaseBranchPatternsComponent extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.onAddNewReleaseBranchClicked.bind(this);
        this.confirmReleaseBranchPatternDelete.bind(this);
        this.addNewReleaseBranch.bind(this);

        this.state = {
            newReleaseBranchModalVisible: false,
            releaseBranchIsEditing: {},
            releaseBranchPatternUpdated: {},
            releaseBranchPatternIsValid: {},
            suggestedBranchPattern: '',
            isSuggestedBranchPatternLoading: false,
        };
    }

    static getDerivedStateFromProps(nextProps: Props, prevState: State) {
        const updatedReleaseBranchPatternUpdated = {
            ...prevState.releaseBranchPatternUpdated,
        };

        const updatedReleaseBranchPatternIsEditing = {
            ...prevState.releaseBranchIsEditing,
        };

        forIn(nextProps.releaseBranchPatternUpdated, (didUpdate, id) => {
            if (didUpdate) {
                // clear stored pattern updates
                // reset field to isEditing = false
                updatedReleaseBranchPatternUpdated[id] = null;
                updatedReleaseBranchPatternIsEditing[id] = false;
            }
        });

        return {
            newReleaseBranchModalVisible:
                nextProps.newReleaseBranchPatternAdded ? false : prevState.newReleaseBranchModalVisible,
            releaseBranchPatternUpdated: updatedReleaseBranchPatternUpdated,
            releaseBranchIsEditing: updatedReleaseBranchPatternIsEditing,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.newReleaseBranchPatternFailed && this.props.newReleaseBranchPatternFailed) {
            globalMessage.error(this.props.newReleaseBranchPatternFailed);
        }

        if (!prevProps.newReleaseBranchPatternAdded && this.props.newReleaseBranchPatternAdded) {
            globalMessage.success('New branch pattern added!');
        }

        // Error updating pattern
        forIn(this.props.releaseBranchPatternSaveError, (error, id) => {
            const prevError = prevProps.releaseBranchPatternSaveError[id];

            if (!prevError && error) {
                globalMessage.error(error);
            }
        });
    }

    onAddNewReleaseBranchClicked = () => {
        this.setState({
            ...this.state,
            newReleaseBranchModalVisible: true,
        });

        if (this.state.suggestedBranchPattern === '') {
            this.loadSuggestedBranchPattern();
        }
    };

    onCancelAddNewReleaseBranch = () => {
        this.setState({
            ...this.state,
            newReleaseBranchModalVisible: false,
        });
    };

    addNewReleaseBranch = (pattern: string, isHotfix: boolean) => {
        const newReleaseBranchPatternRequest: NewReleaseBranchPatternRequest = {
            isHotfix,
            pattern,
            orgId: this.props.orgId,
            appId: this.props.appId,
        };

        this.props.addNewReleaseBranch(newReleaseBranchPatternRequest);
    };

    confirmReleaseBranchPatternDelete = (id: number) => {
        const deleteReleaseBranchPatternRequest: DeleteReleaseBranchPatternRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            branchPatternId: id,
        };

        this.props.deleteReleaseBranchPattern(deleteReleaseBranchPatternRequest);
    };

    saveReleaseBranchPattern = (id: number) => {
        const saveReleaseBranchPatternRequest: UpdateReleaseBranchPatternRequest = {
            orgId: this.props.orgId,
            branchPatternId: id,
            pattern: this.state.releaseBranchPatternUpdated[id],
            appId: this.props.appId,
        };

        this.props.updateReleaseBranchPattern(saveReleaseBranchPatternRequest, id);
    };

    loadSuggestedBranchPattern = () => {
        const request: AppEntityRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
        };

        this.setState({
            ...this.state,
            newReleaseBranchModalVisible: true,
            isSuggestedBranchPatternLoading: true,
        });

        API.getSuggestedReleaseBranchPattern(request, this.props.credentials)
            .then((response) => {
                this.setState({
                    ...this.state,
                    suggestedBranchPattern: response,
                });
            })
            .catch(() => {
                return;
            })
            .finally(() => {
                this.setState({
                    ...this.state,
                    isSuggestedBranchPatternLoading: false,
                });
            });
    };

    renderReleaseBranchPatternField: (record: ReleaseBranchPattern) => JSX.Element = (record) => {
        const releaseBranchEditing = this.state.releaseBranchIsEditing[record.id];
        const showIsDefault = this.props.releaseBranchPatterns.length > 1 && record.isDefault;
        const doesOtherPatternOfSameTypeExist = this.props.releaseBranchPatterns.some(
            (existing) => existing.id != record.id && existing.isHotfix == record.isHotfix,
        );
        const doesOtherVersionedPatternExistForNonHotfix = this.props.releaseBranchPatterns.some(
            (existing) => existing.id != record.id && !existing.isHotfix && !existing.isStatic,
        );
        const shouldBeVersioned =
            doesOtherPatternOfSameTypeExist || (doesOtherVersionedPatternExistForNonHotfix && record.isHotfix);

        const rules = [
            { required: true, message: '' },
            {
                validator: (rule, value) => {
                    const pattern = value as string;

                    return validatePattern(
                        pattern,
                        'Release branch',
                        requireAtLeastOneOfTokensForBranch(shouldBeVersioned),
                        DisallowedTokensForBranch,
                    );
                },
            },
            {
                validator: (rule, value) => {
                    const pattern = value as string;

                    if (this.props.releaseBranchPatterns.some((existing) => existing.pattern == pattern)) {
                        return Promise.reject();
                    }

                    return Promise.resolve();
                },
            },
        ];

        return (
            <Space>
                <AppSettingsEditableInputComponent
                    id={record.id}
                    isEditing={releaseBranchEditing}
                    defaultValue={record.pattern}
                    textProps={{ code: true }}
                    rules={rules}
                    onValueChanged={(id, value, isValid) => {
                        const releaseBranchPatternUpdated = {
                            ...this.state.releaseBranchPatternUpdated,
                            [id]: value,
                        };

                        const releaseBranchPatternIsValid = {
                            ...this.state.releaseBranchPatternIsValid,
                            [id]: isValid,
                        };
                        this.setState({
                            releaseBranchPatternUpdated,
                            releaseBranchPatternIsValid,
                        });
                    }}
                />
                {showIsDefault && <Text style={{ color: runwayColors.statusYellow }}>Default</Text>}
            </Space>
        );
    };

    renderReleaseBranchPatternActions: (record: ReleaseBranchPattern) => JSX.Element = (record) => {
        const releaseBranchEditing = this.state.releaseBranchIsEditing[record.id];
        const branchPatternIsValid = this.state.releaseBranchPatternIsValid[record.id];

        return (
            <AppSettingsActionsComponent
                recordId={record.id}
                isEditing={releaseBranchEditing}
                saveDisabled={!branchPatternIsValid}
                saveIsLoading={this.props.releaseBranchPatternSaveIsLoading[record.id]}
                deleteConfirmationText="Are you sure you want to delete this release branch pattern?"
                saveButtonOnClick={(id) => this.saveReleaseBranchPattern(id as number)}
                editCancelButtonClicked={(id) => {
                    const releaseBranchIsEditing = {
                        ...this.state.releaseBranchIsEditing,
                        [id]: false,
                    };

                    const releaseBranchPatternIsValid = {
                        ...this.state.releaseBranchPatternIsValid,
                        [id]: null,
                    };

                    const releaseBranchPatternUpdated = {
                        ...this.state.releaseBranchPatternUpdated,
                        [id]: null,
                    };

                    this.setState({
                        releaseBranchIsEditing,
                        releaseBranchPatternUpdated,
                        releaseBranchPatternIsValid,
                    });
                }}
                editStartButtonClicked={(id) => {
                    const releaseBranchIsEditing = {
                        ...this.state.releaseBranchIsEditing,
                        [id]: true,
                    };
                    this.setState({ releaseBranchIsEditing });
                }}
                deleteConfirm={(id) => this.confirmReleaseBranchPatternDelete(id as number)}
                editPermissionedActionType={RunwayUserAction.UAAddRemoveEditReleaseBranchPatterns}
                deletePermissionsedActionType={RunwayUserAction.UAAddRemoveEditReleaseBranchPatterns}
            />
        );
    };

    render() {
        return (
            <div ref={this.props.refProp} style={this.props.style}>
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Space direction="vertical" size="small">
                            <h4 style={styles.appSettingsSectionTitleStyle}>Release branch</h4>
                            <Text type="secondary">
                                The branch or branches you use to isolate production-ready code. You may define
                                tokenized branch patterns which include the release version, or define a single static
                                branch. Read more about tokenized patterns in our{' '}
                                <Typography.Link
                                    href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                                    target="_blank"
                                >
                                    docs.
                                </Typography.Link>
                            </Text>
                        </Space>
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAAddRemoveEditReleaseBranchPatterns}
                            style={{ float: 'right' }}
                            icon={<PlusOutlined />}
                            onClick={this.onAddNewReleaseBranchClicked}
                            disabled={this.props.isAddNewReleaseBranchDisabled}
                        >
                            Add
                        </PermissionedActionButton>
                    </div>
                    <Table
                        bordered
                        style={{ marginTop: 12, marginBottom: 12 }}
                        size="small"
                        locale={{
                            emptyText: 'No branch patterns added yet',
                        }}
                        columns={[
                            {
                                title: 'Release type',
                                key: 'releaseType',
                                dataIndex: 'isHotfix',
                                render: (isHotfix: boolean) => {
                                    const releaseTypeDisplay =
                                        isHotfix ? 'hotfixes'
                                        : this.props.hasHotfixPattern ? 'all types (except hotfixes)'
                                        : 'all types';

                                    return <Text>{releaseTypeDisplay}</Text>;
                                },
                                width: 200,
                            },
                            {
                                title: 'Release branch',
                                render: this.renderReleaseBranchPatternField,
                            },
                            {
                                title: '',
                                align: 'right',
                                render: this.renderReleaseBranchPatternActions,
                            },
                        ]}
                        dataSource={this.props.releaseBranchPatterns.map((releaseBranchPattern, index) => {
                            return {
                                ...releaseBranchPattern,
                                key: index,
                            };
                        })}
                        pagination={false}
                    />
                </div>
                <NewReleaseBranchPatternModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    open={this.state.newReleaseBranchModalVisible}
                    isLoading={this.props.newReleaseBranchPatternIsLoading}
                    onCancelAddNewReleaseBranchPattern={this.onCancelAddNewReleaseBranch}
                    onOkNewReleaseBranchPattern={this.addNewReleaseBranch}
                    suggestedBranchPattern={this.state.suggestedBranchPattern}
                    suggestedBranchPatternIsLoading={this.state.isSuggestedBranchPatternLoading}
                />
            </div>
        );
    }
}

interface ReleaseBranchPatternsComponentProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
    style?: React.CSSProperties;
}

const mapProps = (state: RootState, ownProps: ReleaseBranchPatternsComponentProps) => {
    const { appId, orgId } = ownProps;

    const app = selectApp(state, appId);

    const newReleaseBranchPatternIsLoading = getIsLoading(AppSettingsUpdatable.NewReleaseBranchPattern, state);
    const newReleaseBranchPatternAdded = getUpdated(AppSettingsUpdatable.NewReleaseBranchPattern, state);
    const newReleaseBranchPatternFailed = getError(AppSettingsUpdatable.NewReleaseBranchPattern, state);

    const credentials = selectAuthCredentials(state);

    const releaseBranchPatternSaveIsLoading: NumberKeyedDictionary<boolean> = {};
    const releaseBranchPatternUpdated: NumberKeyedDictionary<boolean> = {};
    const releaseBranchPatternSaveError: NumberKeyedDictionary<string> = {};

    let hasHotfixPattern = false;
    let hasStaticNonHotfixPattern = false;
    let hasStaticHotfixPattern = false;
    forEach(app.appSettings.releaseBranchPatterns, (releaseBranchPattern) => {
        if (releaseBranchPattern.isHotfix) {
            hasHotfixPattern = true;
        }
        if (releaseBranchPattern.isStatic) {
            if (releaseBranchPattern.isHotfix) {
                hasStaticHotfixPattern = true;
            } else {
                hasStaticNonHotfixPattern = true;
            }
        }
        const saveIsLoading = getIsLoading(
            AppSettingsUpdatable.UpdateReleaseBranchPattern,
            state,
            releaseBranchPattern.id,
        );
        const updated = getUpdated(AppSettingsUpdatable.UpdateReleaseBranchPattern, state, releaseBranchPattern.id);
        const updateError = getError(AppSettingsUpdatable.UpdateReleaseBranchPattern, state, releaseBranchPattern.id);

        releaseBranchPatternSaveIsLoading[releaseBranchPattern.id] = saveIsLoading;
        releaseBranchPatternUpdated[releaseBranchPattern.id] = updated;
        releaseBranchPatternSaveError[releaseBranchPattern.id] = updateError;
    });

    return {
        appName: app.appSettings.appName,
        releaseBranchPatterns: app.appSettings.releaseBranchPatterns,
        newReleaseBranchPatternIsLoading,
        newReleaseBranchPatternAdded,
        newReleaseBranchPatternFailed,
        releaseBranchPatternUpdated,
        releaseBranchPatternSaveIsLoading,
        releaseBranchPatternSaveError,
        credentials,
        appId,
        orgId,
        refProp: ownProps.refProp,
        hasHotfixPattern,
        style: ownProps.style,
        isAddNewReleaseBranchDisabled: hasStaticNonHotfixPattern && hasStaticHotfixPattern,
    };
};

const mapDispatch = (dispatch) => {
    return {
        addNewReleaseBranch: (request: NewReleaseBranchPatternRequest) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.NewReleaseBranchPattern, request));
        },
        updateReleaseBranchPattern: (request: UpdateReleaseBranchPatternRequest, id: number) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateReleaseBranchPattern, request, id));
        },
        deleteReleaseBranchPattern: (request: DeleteReleaseBranchPatternRequest) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.DeleteReleaseBranchPattern, request));
        },
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(ReleaseBranchPatternsComponent);
